import {
  Pressable,
  ScrollView,
  StyleProp,
  View,
  ViewStyle,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Icon } from '@/components/Icon';
import { Text } from '@/components/Text';
import colors from '@/config/colors';
import RNModal from 'react-native-modal';
import { useOverlayGuard } from '@/hooks/useOverlayGuard';
import { styles } from './style';
import { IS_WEB } from '@/utils/platform';

interface ModalProps {
  title?: string;
  isVisible: boolean;
  onClose?: () => void;
  confirmActionButton?: React.ReactNode;
  cancelActionButton?: React.ReactNode;
  children: React.ReactNode;
  onModalHide?: () => void;
  coverKeyboard?: boolean;
  animationInTiming?: number;
  animationOutTiming?: number;
  customStyle?: StyleProp<ViewStyle>;
  isContentScrollable?: boolean;
}

/*
* This Modal component is based on react-native-modal
* Provides a customizable modal with title, close button, and action buttons.
* Limitation: Can't display multiple modals at the same time. If
* this is needed, you will have to use a setTimeout to show the next modal after the first one is closed.

*/
export const Modal = ({
  title,
  isVisible,
  onClose,
  confirmActionButton,
  cancelActionButton,
  children,
  animationInTiming,
  animationOutTiming,
  onModalHide,
  customStyle,
  isContentScrollable,
}: ModalProps) => {
  useOverlayGuard(isVisible);
  return (
    <RNModal
      isVisible={isVisible}
      avoidKeyboard
      propagateSwipe
      style={[styles.container, !IS_WEB && { margin: 0 }]}
      statusBarTranslucent
      animationInTiming={animationInTiming}
      animationOutTiming={animationOutTiming}
      onModalHide={onModalHide}
    >
      <SafeAreaView style={[styles.modal, customStyle]}>
        {onClose && (
          <Pressable
            onPress={onClose}
            style={styles.closeButton}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Icon
              name="x"
              size={24}
              style={{
                color: colors.newUI.therapist.primaryBlue80,
              }}
            />
          </Pressable>
        )}
        {title && <Text style={styles.title}>{title}</Text>}
        <View style={[!IS_WEB && { maxWidth: '80%' }, { flexShrink: 1 }]}>
          {isContentScrollable ? (
            <ScrollView style={{ padding: 2 }}>{children}</ScrollView>
          ) : (
            children
          )}
          <View
            style={
              cancelActionButton && confirmActionButton
                ? styles.actionButtons
                : styles.singleActionButton
            }
          >
            {cancelActionButton && <View>{cancelActionButton}</View>}
            {confirmActionButton && <View>{confirmActionButton}</View>}
          </View>
        </View>
      </SafeAreaView>
    </RNModal>
  );
};
